import React, { useState, useEffect } from 'react';
import { X, Heart, Sparkles, Crown, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DailyTipPopup = ({ onClose, user }) => {
  const [dailyTip, setDailyTip] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDailyTip();
  }, []);

  const fetchDailyTip = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/daily-tip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyTip(response.data.tip || "Communication is the foundation of every strong relationship. Make time today to truly listen to your partner.");
    } catch (error) {
      console.error('Error fetching daily tip:', error);
      // Fallback tip
      setDailyTip("Small gestures of love matter more than grand gestures. Show appreciation today!");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    onClose();
    // Use window.location instead of useNavigate to avoid Router context issue
    window.location.href = '/premium';
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all animate-scale-in">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex items-center justify-center mb-3">
            <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
              <Heart className="w-8 h-8 text-white" fill="white" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white text-center mb-1">
            Welcome Back, {user?.full_name?.split(' ')[0] || 'Love Seeker'}! 💕
          </h2>
          <p className="text-white/90 text-center text-sm">
            Your daily dose of relationship wisdom
          </p>
        </div>

        {/* Daily Tip Section */}
        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <Sparkles className="w-5 h-5 text-rose-500 mr-2" />
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                Today's Relationship Tip
              </h3>
            </div>
            
            {loading ? (
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            ) : (
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed bg-gradient-to-br from-rose-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 p-4 rounded-xl border-l-4 border-rose-500">
                {dailyTip}
              </p>
            )}
          </div>

          {/* Premium Features Teaser */}
          {!user?.is_premium && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-4 mb-4 border border-amber-200 dark:border-gray-500">
              <div className="flex items-start">
                <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-2 rounded-lg mr-3">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-800 dark:text-white mb-1 flex items-center">
                    Unlock Premium Features
                    <TrendingUp className="w-4 h-4 ml-2 text-rose-500" />
                  </h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 mb-3">
                    <li>✨ Advanced relationship insights</li>
                    <li>🎮 Exclusive premium games</li>
                    <li>📊 Detailed compatibility analysis</li>
                    <li>💬 Priority community access</li>
                  </ul>
                  <button
                    onClick={handleUpgrade}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Premium
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-rose-500 to-purple-600 hover:from-rose-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Start Exploring
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyTipPopup;
